import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ShieldCheck, CalendarCheck } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { MaskedLines } from '../motion';
import { ActionLink, TICKMILL_REF_URL, STATS_URL, hideReferral } from './links';

const IMG_HERO = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const stats = hideReferral(t('dcapital.stats'));

  return (
    <section ref={ref} className="relative min-h-[95vh] flex items-center pt-28 pb-20 overflow-hidden" data-testid="dc-hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMG_HERO} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/70 via-[#0F172A]/90 to-[#0F172A]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-wrap items-center gap-3 mb-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-xs font-semibold text-blue-300" data-testid="dc-badge">
              {t('dcapital.badge')}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-800/50 px-4 py-2 text-xs font-semibold text-slate-300" data-testid="dc-broker-badge">
              <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
              {t('dcapital.broker')}
            </span>
          </motion.div>

          <MaskedLines
            lines={t('dcapital.heroLines')}
            colors={['text-green-400 text-glow-green', 'text-white', 'text-blue-400']}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="dc-hero-sub"
          >
            {t('dcapital.heroSub')}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }} className="mt-9 flex flex-wrap items-center gap-4">
            <ActionLink href={TICKMILL_REF_URL} label={t('dcapital.ctaRegister')} testId="dc-hero-register-btn" big />
            <ActionLink href={STATS_URL} label={t('dcapital.ctaStats')} testId="dc-hero-stats-btn" variant="outline" big />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="lg:col-span-5 grid grid-cols-2 gap-4"
          data-testid="dc-hero-stats"
        >
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`dc-stat-${i}`}
              className="rounded-xl border border-slate-700/70 bg-[#1E293B]/80 backdrop-blur-md p-6 transition-all duration-300 hover:border-blue-500/50 hover:-translate-y-1"
            >
              <p className="font-display text-3xl sm:text-4xl text-white whitespace-nowrap">{s.v}</p>
              <p className="mt-2 text-[11px] font-semibold tracking-widest text-slate-500 uppercase">{s.l}</p>
            </div>
          ))}
          <div className="col-span-2 inline-flex items-center gap-2.5 rounded-xl border border-green-500/30 bg-green-500/5 px-5 py-3.5 text-sm text-green-200" data-testid="dc-weekly-badge">
            <CalendarCheck className="w-4 h-4 text-green-400 shrink-0" />
            {t('dcapital.weekly')} · {t('dcapital.terms')[4].title}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
